import { useEffect } from 'react';
import { useQueries, useQueryClient } from 'react-query';
import axios from 'axios';
import { useRecoilState } from 'recoil';
import { SECOND } from 'time-constants';
import { array, number, object, string } from 'yup';

import { apiConfig } from '@constants';
import { alertsHeadlinesState, hasResetRefetchIntervalState } from '@store';

import { getAlertsDetail } from './useGetAlertsDetail';

import alertPriorities from '../../../assets/data/alertPriority.json';
import { alertShape } from '../../../constants/types/api/alertShape';
import { toQueryString } from '../../../helpers/StringUtils';
import { useGetUserLocation } from '../../useGetUserLocation';
import { includedAlertDescriptions } from '../../useWeatherAlerts';

const alertsArgumentsSchema = object({
  lat: number().required(),
  lng: number().required(),
  language: string().default('en-US'),
});

/**
 * This schema follows the Weather Alerts Headlines - v3.0
 */

// Filter weather alerts only for:
// Severe Thunderstorm Warning, Tornado Warning, Flash Flood Warning.
const getWeatherMapAlerts = (data, maxCount = 3) =>
  [...data]
    .filter(alert => isValidAlert(alert))
    .filter(({ eventDescription }) => includedAlertDescriptions.has(eventDescription))
    .sort(sortByHeadlinePriority)
    .slice(0, maxCount);

const sortByHeadlinePriority = (a, b) => b.headlinePriority - a.headlinePriority;

const isValidAlert = ({ expireTimeUTC }) => expireTimeUTC * SECOND > Date.prototype.getCurrent().getTime();

const alertsSchema = object({
  alerts: array().of(object(alertShape)),
  metadata: object({
    next: number().nullable(),
  }),
}).nullable();

/**
 * Gets Weather Alerts Headlines API URL for given parameters
 * @param {*} param0 alertsArgumentsSchema
 * @returns Weather Alerts Headlines API URL
 */
const getAlertsApiUrl = ({ lat, lng, language }) =>
  [
    apiConfig.TWC_API_BASE,
    apiConfig.TWC_API_ALERTS_HEADLINES,
    toQueryString({
      geocode: `${lat},${lng}`,
      language,
      format: 'json',
      apiKey: apiConfig.TWC_API_KEY,
    }),
  ].join('');

const getAlertsHeadlines = async ({ lat, lng }) => {
  const validateArguments = await alertsArgumentsSchema.validate({ lat, lng });

  const url = getAlertsApiUrl(validateArguments);
  const { data } = await axios.get(url);

  const validateData = await alertsSchema.validate(data);

  /**
   * Alerts without priorities should be ignored everywhere alerts are used.
   * Filters out any alerts returned that don't have a matching eventDescription in the priority list and appends alertPriority properties to alerts response.
   * Alerts without priorities should be ignored everywhere alerts are used.
   */
  return Promise.all(
    (validateData?.alerts ?? [])
      .filter(alert => alertPriorities.some(({ eventDescription }) => eventDescription === alert.eventDescription))
      .map(alert => ({
        ...alert,
        ...alertPriorities.find(({ eventDescription }) => eventDescription === alert.eventDescription),
      }))
      .map(async ({ detailKey, ...rest }) => ({
        ...rest,
        ...(await getAlertsDetail(detailKey).then(detail => detail?.alertDetail)),
      })),
  );
};

export const useGetAlertsHeadlines = () => {
  const queryClient = useQueryClient();
  const {
    coords: { lat, lng },
  } = useGetUserLocation();
  const locations = useGetUserLocation(false, true);
  const [state, setState] = useRecoilState(alertsHeadlinesState);

  const [hasResetRefetchInterval, setHasResetRefetchInterval] = useRecoilState(hasResetRefetchIntervalState);

  const { alertHeadlines: alertHeadlinesRefetched } = hasResetRefetchInterval;

  // Trigger refetch when Debug date is set
  useEffect(() => {
    if (Date.prototype.debugDate && !alertHeadlinesRefetched) {
      console.log('Invalidating / Refetching Alert Headlines');
      // getAlertsHeadlines-0 is the queryKey for location watching call
      queryClient.invalidateQueries(['getAlertsHeadlines-0', lat, lng]);
      setHasResetRefetchInterval({
        ...hasResetRefetchInterval,
        alertHeadlines: true,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [Date.prototype.debugDate]);

  // using useQueries to fetch alerts for watching and default locations

  const [{ data, error, isLoading, isFetched }, defaultLocRes] = useQueries(
    locations.map((item, index) => ({
      queryKey: [`getAlertsHeadlines-${index}`, item.coords.lat, item.coords.lng],
      queryFn: () =>
        getAlertsHeadlines({
          lat: item.coords.lat,
          lng: item.coords.lng,
        }),
      enabled: Boolean(item.coords.lat && item.coords.lng),
      refetchOnWindowFocus: false,
      refetchInterval: 90 * SECOND,
      retry: false,
    })),
  );

  // alertOverlays for Alert Overlay modal, filtered by weather map alerts
  const alertOverlays = getWeatherMapAlerts(
    // If watching location is different than default location, use the second item's array
    // otherwise use the first item if watching the default location
    locations.length > 1 ? defaultLocRes?.data ?? [] : data ?? [],
  );

  useEffect(() => {
    setState({
      ...state,
      data: data ?? [],
      error,
      isFetched,
      isLoading,
      alertOverlays,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, error, isLoading, isFetched, defaultLocRes?.data]);

  return state;
};
